import { DEFECT_SEVERITIES, EQUIPMENT_STATUSES, type ShipTime } from '@felix/domain';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Drawer,
  Group,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Textarea,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconArrowBackUp,
  IconCheck,
  IconClipboardPlus,
  IconInfoCircle,
  IconLock,
  IconSend,
} from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { Controller, type FieldPath, type UseFormReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../format.js';
import { DateTimeInput } from '../../inputs/DateTimeInput.js';
import { type MasterCapacity, MasterSectionNote, masterLocked } from '../../master-section.js';
import { StatusBadge } from '../../StatusBadge.js';
import { useShell, useUnsavedChanges } from '../../shell-context.js';
import { DefectClosureRoute } from '../defects/DefectParts.js';
import classes from './Condition.module.css';
import type { DefectInput, DefectList, DefectPatch, DefectView } from './condition-types.js';
import { shipLocalToUtc, utcToShipLocal } from './counter-form.js';
import { DefectCloseDialog } from './DefectCloseDialog.js';
import {
  DEFECT_FLOW,
  type DefectFormValues,
  defectEditable,
  defectFlow,
  defectFormSchema,
  defectStateKey,
  defectTone,
  nextDefectStatuses,
} from './defect-view.js';
import { usePmsErrorText } from './EquipmentDrawer.js';
import { classItemOf } from './MaintenancePlanScreen.js';
import pms from './Pms.module.css';
import work from './PmsWork.module.css';
import { PmsPasswordDialog } from './PmsWorkParts.js';
import type { PmsSource } from './source.js';
import type { EquipmentTree, PendingItem, WorkOrderView } from './types.js';

/** Ciddiyet ve iş akışı rozetleri (README tokenları; A17 dili). */
export function DefectBadge({ value, kind }: { value: string; kind: 'state' | 'severity' }) {
  const { t } = useTranslation();
  const group = kind === 'state' ? 'states' : 'severities';
  return (
    <StatusBadge tone={defectTone(value)}>
      {t(`pms.condition.defects.${group}.${value}`, { defaultValue: value })}
    </StatusBadge>
  );
}

/** Onarım iş emrinin özeti: numara ve raporlandı mı (K24). */
export interface RepairInfo {
  woNo: string;
  reported: boolean;
}

/** Arıza kartının değişmez özeti (karar çekmecesi ve detay başlığı): PMS belgesi §15 alanları. */
export function DefectSummary({
  defect,
  equipmentName,
}: {
  defect: DefectView;
  equipmentName: string | null;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`pms.condition.defects.detail.${key}`);
  const rows: [string, string][] = [
    [k('kind'), t(`pms.condition.defects.kinds.${defect.kind}`, { defaultValue: defect.kind })],
    [k('equipment'), equipmentName ?? '—'],
    [k('reportedAt'), formatDateTime(defect.reportedAt)],
    [
      t('pms.condition.defects.columns.severity'),
      t(`pms.condition.defects.severities.${defect.severity}`, { defaultValue: defect.severity }),
    ],
    [k('operationalImpact'), defect.operationalImpact ? k('yes') : k('no')],
    [t('pms.condition.defects.columns.description'), defect.description],
    [k('immediateAction'), defect.immediateAction ?? '—'],
    [k('investigation'), defect.investigation ?? '—'],
    [k('rootCause'), defect.rootCause ?? '—'],
    [k('correctiveAction'), defect.correctiveAction ?? '—'],
    [k('testResult'), defect.testResult ?? '—'],
    [k('conditionAfter'), defect.conditionAfter ? t(`pms.statuses.${defect.conditionAfter}`) : '—'],
    ...(defect.noRepairReason
      ? [[k('noRepairReason'), defect.noRepairReason] as [string, string]]
      : []),
    ...(defect.classNotificationRef
      ? [[k('classNotificationRef'), defect.classNotificationRef] as [string, string]]
      : []),
    ...(defect.classNotificationWaiver
      ? [[k('classNotificationWaiver'), defect.classNotificationWaiver] as [string, string]]
      : []),
  ];
  return (
    <dl className={pms.detailRows}>
      {rows.map(([label, value]) => (
        <div key={label} className={pms.detailRow}>
          <dt>{label}</dt>
          <dd className={pms.notes}>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** İş akışı şeridi (PMS belgesi §15): geçilen adımlar onaylı, bulunulan `aria-current`. */
export function DefectFlow({ defect }: { defect: Pick<DefectView, 'status' | 'closure'> }) {
  const { t } = useTranslation();
  const states = defectFlow(defect);
  return (
    <ol className={classes.flow} aria-label={t('pms.condition.defects.flowLabel')}>
      {DEFECT_FLOW.map((key, index) => (
        <li
          key={key}
          data-state={states[index] ?? undefined}
          aria-current={states[index] === 'current' ? 'step' : undefined}
        >
          {states[index] === 'done' ? <IconCheck size={12} stroke={3} aria-hidden /> : null}
          {t(`pms.condition.defects.states.${key}`)}
        </li>
      ))}
    </ol>
  );
}

interface WorkflowValues {
  status: string;
  investigation: string;
  rootCause: string;
  correctiveAction: string;
  testResult: string;
  conditionAfter: string;
  noRepairReason: string;
  classNotificationRef: string;
  classNotificationWaiver: string;
}

const workflowOf = (d: DefectView): WorkflowValues => ({
  status: d.status,
  investigation: d.investigation ?? '',
  rootCause: d.rootCause ?? '',
  correctiveAction: d.correctiveAction ?? '',
  testResult: d.testResult ?? '',
  conditionAfter: d.conditionAfter ?? '',
  noRepairReason: d.noRepairReason ?? '',
  classNotificationRef: d.classNotificationRef ?? '',
  classNotificationWaiver: d.classNotificationWaiver ?? '',
});

/** Formdaki değişen alanlar (boş metin `null`; sunucu boşlukları yeniden düzenler). */
export function workflowPatch(defect: DefectView, values: WorkflowValues): DefectPatch {
  const before = workflowOf(defect);
  const patch: Record<string, unknown> = {};
  for (const key of Object.keys(values) as (keyof WorkflowValues)[]) {
    if (values[key] === before[key]) continue;
    patch[key] = key === 'status' ? values[key] : values[key].trim() === '' ? null : values[key];
  }
  return patch as DefectPatch;
}

export interface DefectDetailProps {
  defect: DefectView;
  scope: 'web' | 'ship';
  /** Gemide düzenleme yetkisi (`defects.edit`) ve kayıt kilitli değil. */
  editable: boolean;
  equipmentName: string | null;
  /** Ekipman ya da atası klas kalemi (breakdown klas bildirimi; ADR 0029 C9). */
  classEquipment: boolean;
  repair: RepairInfo | null;
  /** Gemide kişinin vereceği kapanış kararı (zil maddesi). */
  decision: PendingItem | null;
  onSave: (patch: DefectPatch) => Promise<void>;
  onOpenRepair: () => void;
  onViewRepair: () => void;
  onSubmit: () => void;
  onDecide: (action: 'approved' | 'returned') => void;
}

/**
 * Arıza detayı (plan M7 Task 21b; PMS belgesi §15–§16): şerit, özet, iş akışı formu (inceleme, kök
 * neden, düzeltici faaliyet, test, onarım sonrası durum, klas bildirimi), onarım iş emri ve
 * "Submit for closure". Kapanışı bekleyen ya da kapanmış kayıt kilitlidir [NR496 4.6]; ofiste
 * salt okunur (K13).
 */
export function DefectDetail(props: DefectDetailProps) {
  const { defect } = props;
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`pms.condition.defects.detail.${key}`, options);
  const errorText = usePmsErrorText();
  const state = defectStateKey(defect);
  const locked = state === 'pending' || state === 'closed';
  const editable = props.editable && props.scope === 'ship' && !locked;
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const form = useForm<WorkflowValues>({ defaultValues: workflowOf(defect) });
  // biome-ignore lint/correctness/useExhaustiveDependencies: kayıt sürümü değişince kurulur
  useEffect(() => {
    form.reset(workflowOf(defect));
    setError(null);
  }, [defect.id, defect.rowVersion]);
  useUnsavedChanges(editable && form.formState.isDirty);
  const save = form.handleSubmit(async (values) => {
    setError(null);
    setSaved(false);
    try {
      await props.onSave(workflowPatch(defect, values));
      setSaved(true);
    } catch (failure) {
      setError(failure);
    }
  });
  const text = (name: Exclude<keyof WorkflowValues, 'status' | 'conditionAfter'>) => (
    <Textarea label={k(name)} autosize minRows={2} readOnly={!editable} {...form.register(name)} />
  );
  const nextSteps = nextDefectStatuses(defect.status);
  return (
    <Stack gap="md">
      <div className={classes.defectHead}>
        <h3 className={classes.defectTitle}>
          {defect.defectNo}
          <DefectBadge value={String(defect.severity)} kind="severity" />
          <DefectBadge value={state} kind="state" />
        </h3>
        <DefectFlow defect={defect} />
        {/* Kapanış onay yolu ve kimin onayı beklendiği (ADR 0091) */}
        <DefectClosureRoute
          detail={defect}
          scope={props.scope === 'web' ? 'office' : 'ship'}
          decidable={props.decision !== null}
          showFlow={false}
        />
        {defect.source === 'measurement' || defect.source === 'cm_import' ? (
          <span className={work.note}>{k('fromMeasurement')}</span>
        ) : null}
      </div>
      {props.scope === 'web' ? (
        <Alert color="blue" icon={<IconInfoCircle size={16} />}>
          {k('readOnlyOffice')}
        </Alert>
      ) : state === 'pending' ? (
        <Alert color="orange" icon={<IconLock size={16} />}>
          {k('locked')}
        </Alert>
      ) : state === 'closed' ? (
        <Alert color="green" icon={<IconLock size={16} />}>
          {k('closed')}
        </Alert>
      ) : state === 'returned' ? (
        <Alert color="red" icon={<IconArrowBackUp size={16} />}>
          {k('returned')}
        </Alert>
      ) : null}
      {props.decision ? (
        <Group gap="sm" justify="flex-end">
          <span className={work.note}>{k('decide')}</span>
          <Button
            variant="default"
            color="red"
            leftSection={<IconArrowBackUp size={16} />}
            onClick={() => props.onDecide('returned')}
          >
            {t('pms.work.decision.return')}
          </Button>
          <Button leftSection={<IconCheck size={16} />} onClick={() => props.onDecide('approved')}>
            {t('pms.work.decision.approve')}
          </Button>
        </Group>
      ) : null}
      <DefectSummary defect={defect} equipmentName={props.equipmentName} />

      <fieldset className={pms.fieldset}>
        <legend>{k('repair')}</legend>
        {props.repair ? (
          <Group gap="sm">
            <Button variant="subtle" size="xs" onClick={props.onViewRepair}>
              {k('viewRepair', { wo: props.repair.woNo })}
            </Button>
            <span className={work.note}>
              {props.repair.reported
                ? t('pms.condition.defects.close.repairReported', { wo: props.repair.woNo })
                : t('pms.condition.defects.close.repairNotReported', { wo: props.repair.woNo })}
            </span>
          </Group>
        ) : (
          <Group gap="sm">
            <span className={work.note}>{k('noRepair')}</span>
            {editable && defect.kind !== 'breakdown' ? (
              <Button
                variant="light"
                size="xs"
                leftSection={<IconClipboardPlus size={14} />}
                onClick={props.onOpenRepair}
              >
                {k('openRepair')}
              </Button>
            ) : null}
          </Group>
        )}
      </fieldset>

      {editable ? (
        <form onSubmit={save} noValidate className={work.drawerStack}>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <Controller
              control={form.control}
              name="status"
              render={({ field }) => (
                <Select
                  label={k('next')}
                  data={[defect.status, ...nextSteps].map((value) => ({
                    value,
                    label: t(`pms.condition.defects.states.${value}`),
                  }))}
                  value={field.value}
                  allowDeselect={false}
                  onChange={(value) => field.onChange(value ?? defect.status)}
                />
              )}
            />
            <Controller
              control={form.control}
              name="conditionAfter"
              render={({ field }) => (
                <Select
                  label={k('conditionAfter')}
                  data={EQUIPMENT_STATUSES.map((s) => ({
                    value: s,
                    label: t(`pms.statuses.${s}`),
                  }))}
                  value={field.value || null}
                  onChange={(value) => field.onChange(value ?? '')}
                  clearable
                  clearButtonProps={{ 'aria-label': t('pms.work.clear') }}
                />
              )}
            />
          </SimpleGrid>
          {text('investigation')}
          {text('rootCause')}
          {text('correctiveAction')}
          {text('testResult')}
          {props.repair ? null : text('noRepairReason')}
          {defect.kind === 'breakdown' && props.classEquipment ? (
            <>
              <p className={work.note}>{k('classItem')}</p>
              {text('classNotificationRef')}
              {text('classNotificationWaiver')}
            </>
          ) : null}
          {error ? (
            <Alert color="red" icon={<IconAlertCircle size={16} />} role="alert">
              {errorText(error)}
            </Alert>
          ) : saved ? (
            <Alert color="green" role="status">
              {k('saved')}
            </Alert>
          ) : null}
          <div className={pms.drawerFooter}>
            <Button type="submit" variant="default" loading={form.formState.isSubmitting}>
              {k('save')}
            </Button>
            <Button
              leftSection={<IconSend size={16} />}
              disabled={form.formState.isDirty}
              onClick={props.onSubmit}
            >
              {k('submit')}
            </Button>
          </div>
        </form>
      ) : null}
    </Stack>
  );
}

/** Ekipman ya da atası klas kalemi mi (ADR 0029 C9). */
function classEquipmentOf(tree: EquipmentTree | null, equipmentId: string): boolean {
  return classItemOf(tree, { equipmentId, jobType: '', ceSurvey: false } as never);
}

/**
 * Arıza çekmecesi (`?defect=<id>`): detay, iş akışı, onarım iş emri, kapanışa gönderme ve gemideki
 * karar (K24). Yazmalar gemide (`defectEditor`; K13).
 */
export function DefectDrawer({
  opened,
  defect,
  source,
  tree,
  workOrders,
  pending,
  can,
  onClose,
  onChanged,
  onViewWorkOrder,
}: {
  opened: boolean;
  defect: DefectView | null;
  source: PmsSource;
  /** Sunucunun bildirdiği arıza yetkileri (`DefectList.can`) */
  can: DefectList['can'] | undefined;
  tree: EquipmentTree | null;
  workOrders: readonly WorkOrderView[];
  pending: readonly PendingItem[];
  onClose: () => void;
  onChanged: () => void;
  onViewWorkOrder: (id: string) => void;
}) {
  const { t } = useTranslation();
  const errorText = usePmsErrorText();
  const [closing, setClosing] = useState(false);
  const [deciding, setDeciding] = useState<'approved' | 'returned' | null>(null);
  const [notice, setNotice] = useState<unknown>(null);
  const editor = source.defectEditor;
  // DM-17 kaydı (ADR 0060 §11): ekipman kartından tam bildirime geçiş
  const shell = useShell();
  if (!defect) return null;
  const wo = defect.repairWorkOrderId
    ? workOrders.find((w) => w.id === defect.repairWorkOrderId)
    : undefined;
  const repair: RepairInfo | null = defect.repairWorkOrderId
    ? { woNo: wo?.woNo ?? '—', reported: Boolean(wo?.record) }
    : null;
  const decision =
    source.scope === 'ship'
      ? (pending.find((p) => p.recordId === defect.id && !p.office) ?? null)
      : null;
  const classEquipment = classEquipmentOf(tree, defect.equipmentId);
  const name = tree?.nodes.find((n) => n.id === defect.equipmentId)?.name ?? null;
  return (
    <>
      <Drawer
        opened={opened}
        onClose={onClose}
        position="right"
        size={620}
        title={<span className={pms.drawerTitle}>{defect.defectNo}</span>}
        closeButtonProps={{ 'aria-label': t('pms.drawer.close') }}
      >
        {notice ? (
          <Alert
            color="red"
            mb="sm"
            withCloseButton
            closeButtonLabel={t('pms.work.detail.dismiss')}
            onClose={() => setNotice(null)}
            role="alert"
          >
            {errorText(notice)}
          </Alert>
        ) : null}
        {shell ? (
          <Button
            variant="light"
            size="xs"
            mb="sm"
            onClick={() => shell.navigate(`/pms/defects?id=${defect.id}`)}
          >
            {t('pms.condition.defects.detail.openReport')}
          </Button>
        ) : null}
        <DefectDetail
          defect={defect}
          scope={source.scope}
          editable={defectEditable({ scope: source.scope, editor: Boolean(editor), can })}
          equipmentName={name}
          classEquipment={classEquipment}
          repair={repair}
          decision={decision}
          onSave={async (patch) => {
            await editor?.update(defect.id, defect.rowVersion, patch);
            onChanged();
          }}
          onOpenRepair={async () => {
            try {
              const result = await editor?.repairWorkOrder(defect.id);
              onChanged();
              if (result) onViewWorkOrder(result.workOrder.id);
            } catch (error) {
              setNotice(error);
            }
          }}
          onViewRepair={() => {
            if (defect.repairWorkOrderId) onViewWorkOrder(defect.repairWorkOrderId);
          }}
          onSubmit={() => setClosing(true)}
          onDecide={setDeciding}
        />
      </Drawer>
      {editor ? (
        <DefectCloseDialog
          opened={closing}
          defect={defect}
          classEquipment={classEquipment}
          repair={repair}
          master={can?.master}
          onClose={() => setClosing(false)}
          onSubmit={async (patch, password) => {
            if (Object.keys(patch).length > 0)
              await editor.update(defect.id, defect.rowVersion, patch);
            await editor.submit(defect.id, password);
            setClosing(false);
            onChanged();
          }}
        />
      ) : null}
      {decision && editor ? (
        <PmsPasswordDialog
          opened={deciding !== null}
          title={
            deciding === 'returned'
              ? t('pms.work.decision.returnTitle')
              : t('pms.work.decision.approveTitle')
          }
          body={t(
            deciding === 'returned'
              ? 'pms.work.decision.returnBody'
              : 'pms.work.decision.approveBody',
            {
              name: t(
                `pms.work.decision.title.${defect.kind === 'breakdown' ? 'breakdown' : 'defect'}`,
              ),
            },
          )}
          reason={deciding === 'returned' ? 'required' : 'optional'}
          confirmLabel={
            deciding === 'returned' ? t('pms.work.decision.return') : t('pms.work.decision.approve')
          }
          danger={deciding === 'returned'}
          reauthKind={defect.kind === 'breakdown' ? 'breakdown_report' : 'defect_report'}
          onClose={() => setDeciding(null)}
          onConfirm={async ({ reason, password }) => {
            if (!deciding) return null;
            await editor.decide(defect.id, {
              contentHash: decision.contentHash,
              submissionId: decision.submissionId,
              step: decision.step,
              decision: deciding,
              ...(reason ? { reason } : {}),
              ...(password ? { password } : {}),
            });
            setDeciding(null);
            onChanged();
            return null;
          }}
        />
      ) : null}
    </>
  );
}

/** Yeni arıza formunun gövdesi (bileşen testi sunucu çizimiyle). */
export function DefectFormFields({
  form,
  options,
  maxDate,
  master,
}: {
  form: UseFormReturn<DefectFormValues>;
  options: { value: string; label: string }[];
  /** Gemi saatiyle şimdi: bildirim anı gelecekte olamaz. */
  maxDate: string;
  /** Part B'yi (anlık önlem) yazma sıfatı (ADR 0127 §2); eski sunucuda yok. */
  master?: MasterCapacity | undefined;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`pms.condition.defects.form.${key}`);
  const fieldError = (name: FieldPath<DefectFormValues>) => {
    const message = form.formState.errors[name as keyof DefectFormValues]?.message;
    return message
      ? t(`pms.errors.${message}`, {
          defaultValue: t(`pms.fieldErrors.${message}`, {
            defaultValue: t('pms.errors.VALIDATION_FAILED'),
          }),
        })
      : undefined;
  };
  const kind = form.watch('kind');
  return (
    <Stack gap="sm">
      {kind === 'breakdown' ? (
        <Alert color="orange" icon={<IconInfoCircle size={16} />}>
          {k('breakdownHint')}
        </Alert>
      ) : null}
      <Controller
        control={form.control}
        name="equipmentId"
        render={({ field }) => (
          <Select
            label={k('equipment')}
            required
            searchable
            data={options}
            value={field.value || null}
            onChange={(value) => field.onChange(value ?? '')}
            error={fieldError('equipmentId')}
          />
        )}
      />
      <Textarea
        label={k('description')}
        required
        autosize
        minRows={2}
        {...form.register('description')}
        error={fieldError('description')}
      />
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <Controller
          control={form.control}
          name="severity"
          render={({ field }) => (
            <Select
              label={k('severity')}
              data={DEFECT_SEVERITIES.map((s) => ({
                value: s,
                label: t(`pms.condition.defects.severities.${s}`),
              }))}
              value={field.value}
              allowDeselect={false}
              onChange={(value) => field.onChange(value ?? 'major')}
            />
          )}
        />
        <Controller
          control={form.control}
          name="reportedAt"
          render={({ field }) => (
            <DateTimeInput
              label={k('reportedAt')}
              required
              value={field.value}
              onChange={field.onChange}
              maxDate={maxDate}
              error={fieldError('reportedAt')}
            />
          )}
        />
      </SimpleGrid>
      <Controller
        control={form.control}
        name="operationalImpact"
        render={({ field }) => (
          <Switch
            label={k('operationalImpact')}
            checked={field.value}
            onChange={(event) => field.onChange(event.currentTarget.checked)}
          />
        )}
      />
      <Textarea
        label={k('immediateAction')}
        autosize
        minRows={2}
        readOnly={masterLocked(master)}
        {...form.register('immediateAction')}
      />
      <MasterSectionNote capacity={master} />
    </Stack>
  );
}

/** "+ Report Defect / Breakdown" (gemide; K13): PMS belgesi §15 alanları (RHF + Zod). */
export function NewDefectDrawer({
  opened,
  kind,
  tree,
  timeZone,
  now,
  master,
  onClose,
  onSubmit,
}: {
  opened: boolean;
  /** Part B'yi yazma sıfatı (ADR 0127 §2); eski sunucuda yok. */
  master?: MasterCapacity | undefined;
  kind: 'defect' | 'breakdown';
  tree: EquipmentTree | null;
  timeZone: ShipTime;
  now: () => Date;
  onClose: () => void;
  onSubmit: (input: DefectInput & { reportedAt?: string }) => Promise<void>;
}) {
  const { t } = useTranslation();
  const errorText = usePmsErrorText();
  const [error, setError] = useState<unknown>(null);
  const serverNow = now().toISOString();
  const schema = useMemo(() => defectFormSchema({ serverNow, timeZone }), [serverNow, timeZone]);
  const empty = (): DefectFormValues => ({
    kind,
    equipmentId: '',
    description: '',
    severity: kind === 'breakdown' ? 'critical' : 'major',
    operationalImpact: kind === 'breakdown',
    immediateAction: '',
    reportedAt: utcToShipLocal(now(), timeZone),
  });
  const form = useForm<DefectFormValues>({ resolver: zodResolver(schema), defaultValues: empty() });
  useUnsavedChanges(opened && form.formState.isDirty);
  // biome-ignore lint/correctness/useExhaustiveDependencies: pencere açılınca kurulur
  useEffect(() => {
    if (!opened) return;
    setError(null);
    form.reset(empty());
  }, [opened, kind]);
  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const reportedAt = shipLocalToUtc(values.reportedAt, timeZone);
      await onSubmit({
        kind: values.kind,
        equipmentId: values.equipmentId,
        description: values.description.trim(),
        severity: values.severity,
        operationalImpact: values.operationalImpact,
        // Kaptan değilse Part B gönderilmez (ADR 0127 §2)
        immediateAction:
          masterLocked(master) || values.immediateAction.trim() === ''
            ? null
            : values.immediateAction.trim(),
        ...(reportedAt ? { reportedAt } : {}),
      });
    } catch (failure) {
      setError(failure);
    }
  });
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={560}
      title={
        <span className={pms.drawerTitle}>
          {t(`pms.condition.defects.form.${kind === 'breakdown' ? 'newBreakdown' : 'newDefect'}`)}
        </span>
      }
      closeButtonProps={{ 'aria-label': t('pms.drawer.close') }}
    >
      <form onSubmit={submit} className={pms.drawerForm} noValidate>
        <DefectFormFields
          form={form}
          options={(tree?.nodes ?? []).map((n) => ({
            value: n.id,
            label: `${n.name} · ${n.code}`,
          }))}
          maxDate={utcToShipLocal(serverNow, timeZone)}
        />
        {error ? (
          <Alert color="red" icon={<IconAlertCircle size={16} />} role="alert">
            {errorText(error)}
          </Alert>
        ) : null}
        <div className={pms.drawerFooter}>
          <Button variant="default" onClick={onClose}>
            {t('pms.drawer.cancel')}
          </Button>
          <Button type="submit" loading={form.formState.isSubmitting}>
            {t('pms.condition.defects.form.submit')}
          </Button>
        </div>
      </form>
    </Drawer>
  );
}
